import { useCallback, useState } from "react";
import { useModal } from "@ebay/nice-modal-react";

import DrawerHeader from "ds/components/Drawer/Header";
import DrawerBody from "ds/components/Drawer/Body";
import { AuditTrailEntry } from "types/generated";
import Tab from "ds/components/Tab";
import TabPanel from "ds/components/TabPanel";
import Box from "ds/components/Box";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerSimple from "ds/components/DrawerNew/Simple";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import { AnalyticsPageAuditTrail } from "hooks/useAnalytics/pages/auditTrail";
import useAnalytics from "hooks/useAnalytics";

import AuditTrailEntriesDetailsBody from "./Body";
import AuditTrailEntriesDetailsMetadata from "./Metadata";
import { AuditTrailEntrySuggestions } from "../../constants";

type AuditTrailEntriesDetailsProps = {
  item: AuditTrailEntry;
  applyFilter: (filterName: AuditTrailEntrySuggestions) => (value: string) => void;
};

const AuditTrailEntriesDetails = createDrawer(
  ({ item, applyFilter }: AuditTrailEntriesDetailsProps) => {
    const [tab, setTab] = useState<"metadata" | "body">("metadata");
    const drawer = useModal();

    const trackSegmentAnalyticsEvent = useAnalytics({
      page: AnalyticsPageAuditTrail.Logs,
    });

    const handleApplyFilter = useCallback(
      (filterName: AuditTrailEntrySuggestions) => (value: string) => {
        applyFilter(filterName)(value);
        drawer.hide();
      },
      [applyFilter, drawer]
    );

    const handleClose = () => {
      trackSegmentAnalyticsEvent("Log details drawer closed");
    };

    return (
      <DrawerSimple onClose={handleClose}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Log details" />
          <DrawerCloseIcon />
        </DrawerHeader>

        <DrawerBody fullHeight>
          <Box gap="medium" margin="0 0 large" role="tablist" aria-label="Log details">
            <Tab
              isActive={tab === "metadata"}
              id="metadata"
              onClick={() => setTab("metadata")}
              label="Metadata"
            />
            <Tab isActive={tab === "body"} id="body" onClick={() => setTab("body")} label="Body" />
          </Box>

          <TabPanel isActive={tab === "metadata"} id="metadata">
            <AuditTrailEntriesDetailsMetadata item={item} applyFilter={handleApplyFilter} />
          </TabPanel>
          <TabPanel isActive={tab === "body"} id="body" fullHeight>
            <AuditTrailEntriesDetailsBody value={item.body} />
          </TabPanel>
        </DrawerBody>
      </DrawerSimple>
    );
  }
);

export const showAuditTrailEntriesDetails = createDrawerTrigger(AuditTrailEntriesDetails);
